import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { AreaChart, PieChart, GaugeChart, ChartContainer } from '@/components/charts';
import { useComplianceScore, useComplianceTrends, useComplianceGaps, useCalibrationDue } from '../hooks';
import {
  RefreshCw, ClipboardCheck, Scale, Database, UserCheck,
  AlertTriangle, CheckCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseISO, differenceInDays } from 'date-fns';
import type { ComplianceGap, CalibrationItem } from '../types';

export default function CompliancePage() {
  const { t } = useTranslation('intelligence');

  const { data: score, isLoading: scoreLoading, refetch, isFetching } = useComplianceScore();
  const { data: trends, isLoading: trendsLoading } = useComplianceTrends();
  const { data: gaps, isLoading: gapsLoading } = useComplianceGaps();
  const { data: calibration, isLoading: calibrationLoading } = useCalibrationDue();

  const trendChartData = trends?.map(item => ({
    name: item.date,
    dailyLogRate: item.daily_log_rate,
    calibrationRate: item.calibration_rate,
  })) || [];

  const calibrationItems = calibration as CalibrationItem[] | undefined;
  const calibrationSummary = {
    overdue: calibrationItems?.filter((c: CalibrationItem) => {
      if (!c.due_date) return false;
      return differenceInDays(parseISO(c.due_date), new Date()) < 0;
    }).length || 0,
    dueSoon: calibrationItems?.filter((c: CalibrationItem) => {
      if (!c.due_date) return false;
      const days = differenceInDays(parseISO(c.due_date), new Date());
      return days >= 0 && days <= 30;
    }).length || 0,
    onSchedule: calibrationItems?.filter((c: CalibrationItem) => {
      if (!c.due_date) return false;
      return differenceInDays(parseISO(c.due_date), new Date()) > 30;
    }).length || 0,
  };

  const calibrationChartData = [
    { name: t('compliance.calibration.overdue'), value: calibrationSummary.overdue },
    { name: t('compliance.calibration.dueSoon'), value: calibrationSummary.dueSoon },
    { name: t('compliance.calibration.onSchedule'), value: calibrationSummary.onSchedule },
  ];

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCalibrationStatus = (dueDate: string | undefined) => {
    if (!dueDate) return { status: 'unknown', class: 'bg-gray-100 text-gray-800' };
    const days = differenceInDays(parseISO(dueDate), new Date());
    if (days < 0) return { status: 'overdue', class: 'bg-red-100 text-red-800' };
    if (days <= 30) return { status: 'dueSoon', class: 'bg-yellow-100 text-yellow-800' };
    return { status: 'onSchedule', class: 'bg-green-100 text-green-800' };
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('compliance.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('compliance.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('compliance.cards.dailyLogRate')}
          value={score?.daily_log_rate ? `${score.daily_log_rate.toFixed(1)}%` : '--'}
          icon={<ClipboardCheck className="h-5 w-5" />}
          color="blue"
          loading={scoreLoading}
        />
        <MetricCard
          title={t('compliance.cards.calibrationRate')}
          value={score?.calibration_rate ? `${score.calibration_rate.toFixed(1)}%` : '--'}
          icon={<Scale className="h-5 w-5" />}
          color="green"
          loading={scoreLoading}
        />
        <MetricCard
          title={t('compliance.cards.dataCoverage')}
          value={score?.data_coverage ? `${score.data_coverage.toFixed(1)}%` : '--'}
          icon={<Database className="h-5 w-5" />}
          color="purple"
          loading={scoreLoading}
        />
        <MetricCard
          title={t('compliance.cards.auditReadiness')}
          value={score?.audit_readiness ? `${score.audit_readiness.toFixed(1)}%` : '--'}
          icon={<UserCheck className="h-5 w-5" />}
          color="blue"
          loading={scoreLoading}
        />
      </div>

      {/* Score Gauge + Trends Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('compliance.sections.overallScore')}</CardTitle>
          </CardHeader>
          <CardContent>
            {scoreLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : (
              <GaugeChart
                value={score?.overall_score || 0}
                max={100}
                height={200}
                label={t('compliance.score')}
                suffix="%"
              />
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('compliance.sections.trends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={trendsLoading} height={250}>
              <AreaChart
                data={trendChartData}
                series={[
                  { dataKey: 'dailyLogRate', name: t('compliance.cards.dailyLogRate'), color: '#3498db' },
                  { dataKey: 'calibrationRate', name: t('compliance.cards.calibrationRate'), color: '#27ae60' },
                ]}
                height={220}
              />
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Calibration Chart + Gaps Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('compliance.sections.calibrationStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={calibrationLoading} height={220}>
              <PieChart
                data={calibrationChartData}
                height={200}
                colors={['#e74c3c', '#f39c12', '#27ae60']}
                showLegend
                innerRadius={50}
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {t('compliance.sections.gaps')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('compliance.table.severity')}
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('compliance.table.type')}
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('compliance.table.description')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('compliance.table.count')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gapsLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : gaps && (gaps as ComplianceGap[]).length > 0 ? (
                    (gaps as ComplianceGap[]).map((gap: ComplianceGap, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Badge className={cn('text-xs', getSeverityBadgeClass(gap.severity))}>
                            {t(`compliance.severity.${gap.severity}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{gap.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{gap.description}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="secondary">{gap.count}</Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                        <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                        <p>{t('compliance.noGaps')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calibration Due Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('compliance.sections.calibrationDue')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('compliance.table.sensor')}
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('compliance.table.site')}
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('compliance.table.dueDate')}
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('compliance.table.status')}
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                    {t('compliance.table.daysUntilDue')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {calibrationLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : calibrationItems && calibrationItems.length > 0 ? (
                  calibrationItems.slice(0, 20).map((item: CalibrationItem, index: number) => {
                    const { status, class: statusClass } = getCalibrationStatus(item.due_date);
                    const days = differenceInDays(parseISO(item.due_date), new Date());
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{item.sensor_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{item.site_name}</td>
                        <td className="px-6 py-4 text-center text-sm text-gray-600">
                          {new Date(item.due_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge className={cn('text-xs', statusClass)}>
                            {t(`compliance.calibration.${status}`)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center text-sm">
                          <span className={cn(days < 0 && 'text-red-600 font-semibold')}>
                            {days < 0 ? `${Math.abs(days)} ${t('compliance.daysOverdue')}` : `${days} ${t('compliance.daysRemaining')}`}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {t('compliance.noCalibration')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
